import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronRight,
  ExternalLink,
  HelpCircle,
  ImageIcon,
  Lock,
  MessageSquare,
  Pencil,
  Search,
  SendHorizonal,
  X,
  XCircle,
} from "lucide-react";

import type { SupportTicket } from "@/types";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { SUPPORT_TICKETS_CHANGED_EVENT } from "@/lib/supportEvents";
import {
  CATEGORIES,
  CATEGORY_ICON,
  PRIORITIES,
  PRIORITY_BORDER,
  STATUSES,
  type TicketsPage,
  fmtDate,
  fmtDateTime,
  fmtHours,
} from "@/lib/supportTickets";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import TicketAttachment from "@/components/TicketAttachment";
import { CommentImage, NivaAckBubble, PriorityBadge, ReplyAttachmentButton, StatusBadge, threadWithNiva } from "@/components/SupportTicketParts";
import {
  EmptyPanel,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

const PAGE_SIZE = 10;

const SORT_OPTIONS = [
  { value: "newest",      label: "Newest first" },
  { value: "needs_reply", label: "Needs reply first" },
  { value: "recent",      label: "Latest activity" },
  { value: "priority",    label: "Priority" },
] as const;

const SELECT_CLASS = "h-10 w-full appearance-none rounded-full border border-input bg-background pl-4 pr-9 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40";

interface Assignee { id: string; name: string }

const ROLE_CONFIG: Record<string, { label: string; classes: string; rank: number }> = {
  admin:      { label: "Admin",      classes: "bg-primary/15 text-primary border-primary/30", rank: 4 },
  bl_advisor: { label: "BL Advisor", classes: "bg-violet-500/10 text-violet-600 border-violet-500/30", rank: 3 },
  seller:     { label: "Seller",     classes: "bg-teal-500/10 text-teal-600 border-teal-500/30",       rank: 2 },
  buyer:      { label: "Buyer",      classes: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30", rank: 1 },
};

function RoleBadge({ roles }: { roles?: string[] }) {
  if (!roles || roles.length === 0) {
    return <span className="text-xs text-muted-foreground/60">—</span>;
  }
  // Show the highest-ranked role (admin > BL advisor > seller > buyer)
  const primary = [...roles].sort((a, b) => (ROLE_CONFIG[b]?.rank ?? 0) - (ROLE_CONFIG[a]?.rank ?? 0))[0];
  const cfg = ROLE_CONFIG[primary];
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap", cfg?.classes ?? "bg-slate-100 text-slate-600 border-slate-200")}>
      {cfg?.label ?? primary.replace("_", " ")}
    </span>
  );
}

// ─── modal ────────────────────────────────────────────────────────────────────

function Modal({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  const overlayRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="relative flex h-[90dvh] max-h-[920px] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-2xl">
        {children}
      </div>
    </div>
  );
}

// ─── ticket detail modal ──────────────────────────────────────────────────────

function TicketDetailModal({
  ticket,
  onClose,
  onUpdated,
}: {
  ticket: SupportTicket;
  onClose: () => void;
  onUpdated: (t: SupportTicket) => void;
}) {
  const { user } = useAuth();
  const [tab, setTab] = useState<"details" | "images">("details");
  const [commentText, setCommentText] = useState("");
  const [replyImage, setReplyImage] = useState<string | null>(null);
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [current, setCurrent] = useState(ticket);
  // List rows leave the attachment out, so the full ticket is fetched on open.
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [pendingStatus, setPendingStatus] = useState(ticket.status);
  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [savingMeta, setSavingMeta] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const toast = useToast();
  const CategoryIcon = CATEGORY_ICON[current.category] ?? HelpCircle;
  // A closed ticket accepts no replies — reopen it (pick another status) to respond again.
  // Internal notes are always allowed.
  const replyLocked = current.status === "closed" && pendingStatus === "closed" && !internal;

  useEffect(() => {
    let cancelled = false;
    api.get<SupportTicket>(`/support/admin/tickets/${ticket.id}`)
      .then((full) => { if (!cancelled) { setCurrent(full); setPendingStatus(full.status); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    api.get<Assignee[]>(`/support/admin/tickets/${ticket.id}/assignees`)
      .then((list) => { if (!cancelled) setAssignees(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticket.id]);

  function apply(updated: SupportTicket) {
    setCurrent(updated);
    onUpdated(updated);
  }

  async function updateMeta(patch: { priority?: string; assigned_to?: string | null }) {
    setSavingMeta(true);
    try {
      apply(await api.patch<SupportTicket>(`/support/admin/tickets/${current.id}`, patch));
      toast.success("assigned_to" in patch ? "Assignment updated." : "Priority updated.");
    } catch {
      toast.error("Failed to update the ticket.");
    } finally {
      setSavingMeta(false);
    }
  }

  async function handleSendResponse(e: FormEvent) {
    e.preventDefault();
    const hasComment = commentText.trim().length > 0;
    const statusChanged = pendingStatus !== current.status;
    if (!hasComment && !statusChanged) return;
    setSending(true);
    try {
      // One request when there's a comment: the status rides along, so the
      // user gets ONE email/notification, not one per change.
      const updated = hasComment
        ? await api.post<SupportTicket>(`/support/admin/tickets/${current.id}/comments`, {
            comment: commentText.trim(),
            image_url: replyImage,
            is_internal: internal,
            status: statusChanged ? pendingStatus : null,
          })
        : await api.patch<SupportTicket>(`/support/admin/tickets/${current.id}`, { status: pendingStatus });
      apply(updated);
      setCommentText("");
      setReplyImage(null);
      toast.success(hasComment ? (internal ? "Internal note added." : "Response sent.") : "Status updated.");
    } catch {
      toast.error(hasComment ? "Failed to send response." : "Failed to update status.");
    } finally {
      setSending(false);
    }
  }

  const startEdit = (id: string, text: string) => { setEditingId(id); setEditText(text); };
  const cancelEdit = () => { setEditingId(null); setEditText(""); };

  async function saveEdit(commentId: string, original: string) {
    const next = editText.trim();
    if (!next || savingEdit) return;
    if (next === original) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      apply(await api.patch<SupportTicket>(`/support/admin/tickets/${current.id}/comments/${commentId}`, { comment: next }));
      cancelEdit();
      toast.success("Response updated.");
    } catch {
      toast.error("Failed to update response.");
    } finally {
      setSavingEdit(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      {/* ── Header ── */}
      <div className="relative shrink-0 overflow-hidden border-b border-primary/20 bg-gradient-to-br from-primary/12 via-card to-card px-7 py-5">
        {/* soft gold glow accent */}
        <div className="pointer-events-none absolute -right-20 -top-24 h-52 w-52 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary ring-1 ring-primary/25">
              <CategoryIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h2 className="text-xl font-bold text-foreground leading-tight truncate">{current.subject}</h2>
                <span className="inline-flex items-center rounded-md bg-primary/12 px-2 py-0.5 text-xs font-mono font-semibold text-primary ring-1 ring-primary/25 whitespace-nowrap shrink-0">
                  {current.ticket_id}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground capitalize">
                {current.category} · {current.user_name ?? "Unknown"} · {fmtDate(current.created_at)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-foreground/5 text-muted-foreground ring-1 ring-border/60 hover:bg-foreground/10 hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* user + priority row inside header */}
        <div className="relative mt-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg bg-foreground/5 px-3 py-1.5 ring-1 ring-border/60">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/20 text-primary text-[10px] font-bold">
              {(current.user_name ?? "U").charAt(0).toUpperCase()}
            </div>
            <span className="text-xs font-medium text-foreground/90">{current.user_name ?? "Unknown"}</span>
            <span className="text-muted-foreground/50 text-xs">·</span>
            <span className="text-xs text-muted-foreground">{current.user_email ?? ""}</span>
          </div>
          <RoleBadge roles={current.user_roles} />
          <StatusBadge value={current.status} />
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <label className="relative">
              <span className="sr-only">Priority</span>
              <select
                value={current.priority}
                disabled={savingMeta}
                onChange={(e) => updateMeta({ priority: e.target.value })}
                className="h-8 appearance-none rounded-full border border-input bg-background pl-3 pr-8 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
              >
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label} priority</option>)}
              </select>
              <ChevronRight className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3 w-3 rotate-90 text-muted-foreground" />
            </label>
            <label className="relative">
              <span className="sr-only">Assignee</span>
              <select
                value={current.assigned_to ?? ""}
                disabled={savingMeta}
                onChange={(e) => updateMeta({ assigned_to: e.target.value || null })}
                className="h-8 max-w-[200px] appearance-none rounded-full border border-input bg-background pl-3 pr-8 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
              >
                <option value="">Unassigned</option>
                {/* Keep the current assignee selectable even if they are no longer eligible. */}
                {current.assigned_to && !assignees.some((a) => a.id === current.assigned_to) && (
                  <option value={current.assigned_to}>{current.assigned_to_name ?? "Current assignee"}</option>
                )}
                {assignees.map((a) => (
                  <option key={a.id} value={a.id}>{a.id === user?.id ? `${a.name} (me)` : a.name}</option>
                ))}
              </select>
              <ChevronRight className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3 w-3 rotate-90 text-muted-foreground" />
            </label>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex shrink-0 border-b border-border/60 px-7 bg-card">
        {(["details", "images"] as const).map((t) => {
          const isDetails = t === "details";
          const label = isDetails ? "Details" : "Attached Images";
          const Icon = isDetails ? MessageSquare : ImageIcon;
          const badge = isDetails
            ? current.comments.length > 0 ? current.comments.length : null
            : current.has_image ? 1 : null;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                "mr-6 inline-flex items-center gap-1.5 border-b-2 py-3 text-xs font-semibold transition-colors",
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
              {badge !== null && (
                <span className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none",
                  tab === t ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
                )}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Details tab ── */}
      {tab === "details" && (
        <div className="flex flex-1 min-h-0 flex-col gap-5 overflow-y-auto px-7 py-6">

          {/* status switcher */}
          <div className="rounded-xl border border-border/60 bg-muted/10 p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Set Status</p>
            <p className="text-[10px] text-muted-foreground/70 mb-3">Pick a status, then use the button below — with or without a reply.</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => {
                const active = pendingStatus === s.value;
                const changed = active && pendingStatus !== current.status;
                const Icon = s.icon;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setPendingStatus(s.value)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all",
                      active
                        ? s.classes + " ring-2 ring-offset-1 ring-current shadow-sm"
                        : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <Icon className="h-3 w-3" />
                    {s.label}
                    {changed && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-current opacity-80" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* conversation thread */}
          <div className="flex flex-col gap-4">
            {/* User's original message — right-aligned bubble */}
            <div className="flex items-end justify-end gap-2.5">
              <div className="max-w-[80%] space-y-1">
                <p className="text-[10px] font-medium text-muted-foreground text-right pr-1">
                  {current.user_name ?? "User"} · {fmtDateTime(current.created_at)}
                </p>
                <div className="rounded-2xl rounded-br-sm bg-muted/50 border border-border/60 px-4 py-3 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap shadow-sm">
                  {current.description}
                </div>
              </div>
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground/70 ring-1 ring-border/60 text-[11px] font-bold mb-0.5">
                {(current.user_name ?? "U").charAt(0).toUpperCase()}
              </div>
            </div>

            {/* Thread: owner replies right-aligned (user side), admin/support left-aligned,
                with Niva's acknowledgment and unanswered-ticket follow-ups merged in */}
            <div className="space-y-4">
              {threadWithNiva(current).map((c) => {
                if (c.kind === "niva") return <NivaAckBubble key={c.id} text={c.text} at={c.created_at} />;
                  const isOwner = c.admin_id === current.user_id;
                  if (isOwner) {
                    const ownerName = c.admin_name ?? current.user_name ?? "User";
                    return (
                      <div key={c.id} className="flex items-end justify-end gap-2.5">
                        <div className="max-w-[80%] space-y-1">
                          <p className="text-[10px] font-medium text-muted-foreground text-right pr-1">
                            {ownerName} · {fmtDateTime(c.created_at)}{c.edited_at && <span> · edited</span>}
                          </p>
                          <div className="flex flex-col items-end">
                            <div className="rounded-2xl rounded-br-sm bg-muted/50 border border-border/60 px-4 py-3 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap shadow-sm">
                              {c.comment}
                            </div>
                            {c.image_url && <CommentImage url={c.image_url} />}
                          </div>
                        </div>
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground/70 ring-1 ring-border/60 text-[11px] font-bold mb-0.5">
                          {ownerName.charAt(0).toUpperCase()}
                        </div>
                      </div>
                    );
                  }
                  const mine = !!c.admin_id && c.admin_id === user?.id;
                  // A null author is a staff account that has since been deleted.
                  const authorName = c.admin_name ?? (c.admin_id ? "Admin" : "Former staff member");
                  return (
                    <div key={c.id} className="flex items-end gap-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary ring-1 ring-primary/30 text-[11px] font-bold mb-0.5">
                        {authorName.charAt(0).toUpperCase()}
                      </div>
                      <div className="max-w-[80%] space-y-1">
                        <p className="text-[10px] font-medium text-muted-foreground pl-1">
                          {c.is_internal && (
                            <span className="mr-1 inline-flex items-center gap-0.5 font-bold uppercase tracking-wide text-amber-600">
                              <Lock className="h-2.5 w-2.5" /> Internal note ·
                            </span>
                          )}
                          {authorName} · {fmtDateTime(c.created_at)}{c.edited_at && <span> · edited</span>}
                        </p>
                        {editingId === c.id ? (
                          <div className="flex flex-col gap-1.5 min-w-[240px]">
                            <Textarea
                              autoFocus
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(c.id, c.comment); }
                                if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                              }}
                              rows={3}
                              className="resize-none bg-background text-sm"
                            />
                            <div className="flex items-center gap-2 justify-end">
                              <button type="button" onClick={cancelEdit} className="text-[11px] font-semibold text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors">Cancel</button>
                              <Button type="button" size="sm" disabled={!editText.trim() || savingEdit} onClick={() => saveEdit(c.id, c.comment)} className="h-7 px-3 text-[11px]">Save</Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-end gap-1.5">
                            <div className="flex flex-col items-start">
                              <div className={cn(
                                "rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap shadow-sm",
                                c.is_internal
                                  ? "border border-dashed border-amber-500/40 bg-amber-500/10"
                                  : "border border-primary/25 bg-primary/10",
                              )}>
                                {c.comment}
                              </div>
                              {c.image_url && <CommentImage url={c.image_url} />}
                            </div>
                            {mine && (
                              <button
                                type="button"
                                onClick={() => startEdit(c.id, c.comment)}
                                title="Edit response"
                                aria-label="Edit response"
                                className="mb-1 shrink-0 h-6 w-6 rounded-full bg-muted hover:bg-muted-foreground/20 text-muted-foreground flex items-center justify-center opacity-50 hover:opacity-100 transition-all"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
            {current.comments.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border/60 bg-muted/10 py-12 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary/70 ring-1 ring-primary/20">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <p className="text-sm font-medium text-foreground/80">No responses yet</p>
                <p className="mt-1 text-xs text-muted-foreground">Reply below to start the conversation with the user.</p>
              </div>
            )}
          </div>

          {/* reply form */}
          <form onSubmit={handleSendResponse} className="rounded-xl border border-border/60 bg-muted/10 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {internal ? "Add Internal Note" : "Add Response"}
              </p>
              <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <input
                  type="checkbox"
                  checked={internal}
                  onChange={(e) => setInternal(e.target.checked)}
                  className="h-3.5 w-3.5 accent-[var(--color-primary)]"
                />
                <Lock className="h-3 w-3" />
                Internal note (staff only)
              </label>
            </div>
            {replyLocked && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <XCircle className="h-3.5 w-3.5 text-muted-foreground/70" />
                This ticket is closed. Set a different status above to reopen and reply.
              </p>
            )}
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={replyLocked ? "Reopen the ticket to add a response…" : internal ? "Note for other staff — the user won't see this…" : "Write your response to the user…"}
              rows={4}
              disabled={replyLocked}
              className={cn("resize-none bg-background disabled:opacity-60", internal && "border-dashed border-amber-500/50")}
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <ReplyAttachmentButton value={replyImage} onChange={setReplyImage} disabled={sending || replyLocked} />
                {pendingStatus !== current.status && (
                  <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500 inline-block" />
                    Status → <span className="capitalize font-semibold">{pendingStatus.replace("_", " ")}</span>
                  </p>
                )}
              </div>
              <Button
                type="submit"
                disabled={sending || replyLocked || (!commentText.trim() && pendingStatus === current.status)}
                className="gap-2 shrink-0"
              >
                <SendHorizonal className="h-3.5 w-3.5" />
                {sending ? "Sending…" : commentText.trim() ? (internal ? "Add Note" : "Send Response") : "Update Status"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* ── Attached Images tab ── */}
      {tab === "images" && (
        <div className="flex flex-1 min-h-0 flex-col overflow-y-auto px-7 py-6">
          {loadingDetail && current.has_image && !current.image_url ? (
            <div className="flex justify-center py-20"><Loader /></div>
          ) : (
            <TicketAttachment
              url={current.image_url}
              label="Attachment · 1 image"
              emptyHint="The user did not include an image with this ticket."
            />
          )}
        </div>
      )}
    </Modal>
  );
}



// ─── page ─────────────────────────────────────────────────────────────────────

interface TicketStats {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
  closed: number;
  awaiting_reply: number;
  unassigned: number;
  avg_first_response_hours: number | null;
  avg_resolution_hours: number | null;
}

export default function AdminSupportTicketsPage() {
  const [data, setData] = useState<TicketsPage | null>(null);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  /* "Needs a reply" — the queue view. Server-side rather than a client filter
     on the page, or it would only ever find the unanswered tickets that happen
     to be on the page you are looking at. */
  const [onlyAwaiting, setOnlyAwaiting] = useState(false);
  const [filterAssigned, setFilterAssigned] = useState("");
  const [sort, setSort] = useState("newest");
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  // ?ticket=<id> opens that ticket — it's where admin notifications link to.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedTicketId = searchParams.get("ticket");
  const toast = useToast();

  function setTicketParam(id: string | null) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (id) p.set("ticket", id);
        else p.delete("ticket");
        return p;
      },
      { replace: true },
    );
  }

  useEffect(() => {
    if (!linkedTicketId || selectedTicket?.id === linkedTicketId) return;
    api.get<SupportTicket>(`/support/admin/tickets/${linkedTicketId}`)
      .then(setSelectedTicket)
      .catch(() => setTicketParam(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedTicketId]);

  function openTicket(t: SupportTicket) {
    setSelectedTicket(t);
    setTicketParam(t.id);
  }

  function closeTicket() {
    setSelectedTicket(null);
    setTicketParam(null);
  }

  function buildUrl(p: number) {
    const params = new URLSearchParams({ page: String(p), page_size: String(PAGE_SIZE) });
    if (filterStatus)   params.set("status",   filterStatus);
    if (filterPriority) params.set("priority", filterPriority);
    if (filterCategory) params.set("category", filterCategory);
    if (appliedSearch.trim()) params.set("search", appliedSearch.trim());
    if (onlyAwaiting) params.set("awaiting_reply", "true");
    if (filterAssigned) params.set("assigned", filterAssigned);
    params.set("sort", sort);
    return `/support/admin/tickets?${params}`;
  }

  function fetchStats() {
    api.get<TicketStats>("/support/admin/stats").then(setStats).catch(() => {});
  }

  function fetchPage(p: number) {
    setLoading(true);
    api.get<TicketsPage>(buildUrl(p))
      .then(setData)
      .catch(() => toast.error("Failed to load tickets."))
      .finally(() => setLoading(false));
  }

  // fetch stats once on mount (independent of filters/pagination)
  useEffect(() => { fetchStats(); }, []);

  useEffect(() => { fetchPage(page); }, [page, filterStatus, filterPriority, filterCategory, appliedSearch, onlyAwaiting, filterAssigned, sort]);

  // reset to page 1 when filters or the applied search change
  useEffect(() => { setPage(1); }, [filterStatus, filterPriority, filterCategory, appliedSearch, onlyAwaiting, filterAssigned, sort]);

  function handleUpdated(updated: SupportTicket) {
    setData((prev) => prev
      ? { ...prev, items: prev.items.map((t) => (t.id === updated.id ? updated : t)) }
      : prev
    );
    if (selectedTicket?.id === updated.id) setSelectedTicket(updated);
    // Refresh stats after a status change or a reply — both can clear the
    // "needs a reply" marker, and the tile above the table has to agree.
    fetchStats();
    window.dispatchEvent(new Event(SUPPORT_TICKETS_CHANGED_EVENT));
  }

  const total      = stats?.total ?? 0;
  const openCount  = stats?.open ?? 0;
  const inProgress = stats?.in_progress ?? 0;
  const resolved   = stats?.resolved ?? 0;
  const awaiting   = stats?.awaiting_reply ?? 0;

  // result count for the current filter/search (from backend), used for the heading + pagination
  const resultCount = data?.total ?? 0;

  // tickets returned by the backend for the current page/filters/search
  const displayed = data?.items ?? [];

  function applySearch() {
    setAppliedSearch(search);
    setPage(1);
  }

  return (
    <PageShell>
      <PageHero
        eyebrow="Admin"
        title="Support Tickets"
        description="Review and respond to user-submitted support requests."
        aside={
          <div>
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-base font-bold text-foreground">Ticket snapshot</h2>
              {(stats?.unassigned ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => setFilterAssigned(filterAssigned === "unassigned" ? "" : "unassigned")}
                  className="text-right text-[0.7rem] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span className="font-bold text-foreground">{stats?.unassigned}</span> unassigned
                  {filterAssigned === "unassigned" ? " · showing only these" : " · show"}
                </button>
              )}
            </div>
            {/* Its own row above the status counts, and the only one that is a
                button: unanswered tickets are what an admin opens this page to
                act on, not a peer of "how many are resolved". */}
            <button
              type="button"
              onClick={() => setOnlyAwaiting((v) => !v)}
              aria-pressed={onlyAwaiting}
              className={cn(
                "public-glass-card mt-2.5 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors",
                onlyAwaiting ? "ring-2 ring-primary" : "hover:ring-1 hover:ring-primary/40",
              )}
            >
              <span className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
                awaiting > 0 ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
              )}>
                <MessageSquare className="h-3.5 w-3.5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Needs reply
                </span>
                <span className="block text-[0.7rem] leading-tight text-muted-foreground/80">
                  {onlyAwaiting ? "Showing only these — tap to show all" : "New tickets and unanswered replies"}
                </span>
              </span>
              <span className={cn("text-lg font-extrabold", awaiting > 0 ? "text-primary" : "text-foreground")}>{awaiting}</span>
            </button>

            <div className="mt-2 grid grid-cols-3 gap-2">
              {[
                { label: "Total", value: total, color: "text-foreground" },
                { label: "Open", value: openCount, color: "text-primary" },
                { label: "In Progress", value: inProgress, color: "text-amber-600" },
                { label: "Resolved", value: resolved, color: "text-emerald-600" },
                { label: "Avg 1st reply", value: fmtHours(stats?.avg_first_response_hours), color: "text-foreground",
                  title: "Average time from a ticket being raised to support's first public reply" },
                { label: "Avg resolution", value: fmtHours(stats?.avg_resolution_hours), color: "text-foreground",
                  title: "Average time from a ticket being raised to it being resolved or closed (tickets resolved from Oct 2026 on)" },
              ].map((tile) => (
                <div key={tile.label} className="public-glass-card rounded-xl px-2.5 py-2" title={tile.title}>
                  <div className="truncate text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{tile.label}</div>
                  <div className={cn("mt-0.5 text-lg font-extrabold", tile.color)}>{tile.value}</div>
                </div>
              ))}
            </div>
          </div>
        }
      />

      {/* Filters */}
      <PagePanel>
        <div className="mb-4">
          <h2 className="text-base font-bold text-foreground">Filter tickets</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">Combine keyword, status, priority, and category filters to find tickets.</p>
        </div>

        <div className="flex flex-col gap-3">
          {/* Row 1: search + status + priority + button */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  className="pl-9 h-10 text-sm"
                  placeholder="Search subject, user, email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
                />
              </div>
            </div>

            <div className="min-w-[160px]">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</label>
              <div className="relative">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="h-10 w-full appearance-none rounded-full border border-input bg-background pl-4 pr-9 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="">All statuses</option>
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <ChevronRight className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 rotate-90 text-muted-foreground" />
              </div>
            </div>

            <div className="min-w-[150px]">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Priority</label>
              <div className="relative">
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="h-10 w-full appearance-none rounded-full border border-input bg-background pl-4 pr-9 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="">All priorities</option>
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <ChevronRight className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 rotate-90 text-muted-foreground" />
              </div>
            </div>

            <div className="min-w-[160px]">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Category</label>
              <div className="relative">
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="h-10 w-full appearance-none rounded-full border border-input bg-background pl-4 pr-9 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="">All categories</option>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <ChevronRight className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 rotate-90 text-muted-foreground" />
              </div>
            </div>

            <div className="min-w-[150px]">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assigned</label>
              <div className="relative">
                <select value={filterAssigned} onChange={(e) => setFilterAssigned(e.target.value)} className={SELECT_CLASS}>
                  <option value="">Anyone</option>
                  <option value="me">Assigned to me</option>
                  <option value="unassigned">Unassigned</option>
                </select>
                <ChevronRight className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 rotate-90 text-muted-foreground" />
              </div>
            </div>

            <div className="min-w-[170px]">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sort</label>
              <div className="relative">
                <select value={sort} onChange={(e) => setSort(e.target.value)} className={SELECT_CLASS}>
                  {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <ChevronRight className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 rotate-90 text-muted-foreground" />
              </div>
            </div>

            <Button
              type="button"
              onClick={applySearch}
              className="h-10 gap-2 rounded-full px-5"
            >
              <Search className="h-3.5 w-3.5" />
              Search tickets
            </Button>

            {(filterStatus || filterPriority || filterCategory || search || appliedSearch || onlyAwaiting || filterAssigned) && (
              <button
                type="button"
                onClick={() => { setSearch(""); setAppliedSearch(""); setFilterStatus(""); setFilterPriority(""); setFilterCategory(""); setOnlyAwaiting(false); setFilterAssigned(""); }}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
          </div>
        </div>
      </PagePanel>

      {/* Table */}
      <PagePanel>
        <PanelHeading
          title="All Tickets"
          description={data ? `${resultCount} ticket${resultCount !== 1 ? "s" : ""} ${appliedSearch || filterStatus || filterPriority || filterCategory || onlyAwaiting || filterAssigned ? "found" : "total"}` : undefined}
        />

        {loading ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : displayed.length === 0 ? (
          <EmptyPanel
            icon={MessageSquare}
            title="No tickets found"
            description="Try adjusting your filters or wait for users to submit requests."
          />
        ) : (
          <div>
            <div className="overflow-x-auto -mx-5 sm:-mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/30">
                    <th className="py-3 pl-5 sm:pl-6 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden sm:table-cell">Ticket ID</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Subject</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden md:table-cell">User</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden md:table-cell">Role</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden sm:table-cell">Category</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden md:table-cell">Priority</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden lg:table-cell">Submitted</th>
                    <th className="py-3 px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hidden md:table-cell">Replies</th>
                    <th className="py-3 pl-3 pr-5 sm:pr-6 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {displayed.map((ticket) => {
                    const CategoryIcon = CATEGORY_ICON[ticket.category] ?? HelpCircle;
                    return (
                      <tr
                        key={ticket.id}
                        className={cn(
                          "group transition-colors hover:bg-muted/25 border-l-4",
                          PRIORITY_BORDER[ticket.priority] ?? "border-l-transparent",
                          // Unanswered rows read like unread mail: tinted and bold.
                          ticket.awaiting_reply && "bg-primary/[0.06]",
                        )}
                      >
                        <td className="py-3.5 pl-5 sm:pl-6 pr-3 hidden sm:table-cell">
                          <span className="inline-flex items-center rounded-md bg-muted/60 border border-border/60 px-2 py-0.5 text-xs font-mono font-semibold text-muted-foreground whitespace-nowrap">
                            {ticket.ticket_id}
                          </span>
                        </td>
                        <td className="py-3.5 px-3 max-w-[200px]">
                          <div className="flex items-center gap-2">
                            {/* Subject is the one column shown at every width, so
                                the marker lives here rather than in a column of
                                its own that phones would drop. */}
                            {ticket.awaiting_reply && (
                              <span
                                title={ticket.comments.length === 0 ? "New ticket — not answered yet" : "The user replied — waiting on support"}
                                className="shrink-0 rounded-full bg-gold-gradient px-1.5 py-0.5 text-[0.55rem] font-black uppercase tracking-[0.08em] text-primary-foreground"
                              >
                                New
                              </span>
                            )}
                            <p className={cn("truncate text-foreground", ticket.awaiting_reply ? "font-bold" : "font-medium")}>{ticket.subject}</p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate hidden sm:block md:hidden">{ticket.user_name ?? ""}</p>
                        </td>
                        <td className="py-3.5 px-3 hidden md:table-cell">
                          <p className="text-xs font-medium text-foreground">{ticket.user_name ?? "—"}</p>
                          <p className="text-xs text-muted-foreground">{ticket.user_email ?? ""}</p>
                          {ticket.assigned_to_name && (
                            <p className="mt-0.5 text-[10px] font-medium text-primary">→ {ticket.assigned_to_name}</p>
                          )}
                        </td>
                        <td className="py-3.5 px-3 hidden md:table-cell">
                          <RoleBadge roles={ticket.user_roles} />
                        </td>
                        <td className="py-3.5 px-3 hidden sm:table-cell">
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground capitalize">
                            <CategoryIcon className="h-3.5 w-3.5 shrink-0" />
                            {ticket.category}
                          </span>
                        </td>
                        <td className="py-3.5 px-3 hidden md:table-cell">
                          <PriorityBadge value={ticket.priority} />
                        </td>
                        <td className="py-3.5 px-3">
                          <StatusBadge value={ticket.status} />
                        </td>
                        <td className="py-3.5 px-3 text-xs text-muted-foreground whitespace-nowrap hidden lg:table-cell">
                          {fmtDate(ticket.created_at)}
                        </td>
                        <td className="py-3.5 px-3 hidden md:table-cell">
                          {ticket.comments.length > 0 ? (
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 text-xs font-medium",
                                ticket.awaiting_reply ? "text-primary" : "text-muted-foreground",
                              )}
                              title={ticket.awaiting_reply ? "Last message from the user" : "Last message from support"}
                            >
                              <MessageSquare className="h-3 w-3" />
                              {ticket.comments.length}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground/60">—</span>
                          )}
                        </td>
                        <td className="py-3.5 pl-3 pr-5 sm:pr-6 text-right">
                          <button
                            type="button"
                            onClick={() => openTicket(ticket)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-all hover:border-primary/50 hover:bg-primary/5 hover:text-primary group-hover:border-primary/30"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination page={page} totalElements={resultCount} pageSize={PAGE_SIZE} onChange={setPage} className="mt-1 px-5 sm:px-6" />
          </div>
        )}
      </PagePanel>

      {selectedTicket && (
        <TicketDetailModal
          key={selectedTicket.id}
          ticket={selectedTicket}
          onClose={closeTicket}
          onUpdated={handleUpdated}
        />
      )}
    </PageShell>
  );
}
